import { FC, useEffect, useMemo, useState } from 'react';
import { Row, Col, Flex, Typography } from 'antd';
import {
  Container,
  PageTitle,
  StatusCard,
  PieChart,
  DataTable,
  SDropdown,
} from '../../components';
import { StatusEnum } from '../../assets/enums';
import styles from './index.module.scss';
import useHttpService from '../../services/hook';
import {
  IDashboardData,
  IExpiringPolicies,
  IReconciliationDashboard,
} from '../../types';
import {
  getColumnsForExpiringPolicies,
  getColumnsForReconciliationDashboard,
} from './columns';
import { useAppSelector } from '../../store';
import globalStyles from '../../assets/styles/index.module.scss';
import { IEnum } from '../../services/type';
import ReconciliationService from '../../api-clients/ReconciliationService';
import DashboardService from '../../api-clients/DashboardService';
import EnumService from '../../api-clients/EnumService';

const Mainpage: FC = () => {
  const executeService = useHttpService();
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );
  const userInfo = useAppSelector((state) => state.userReducer.userInfo);

  const [id, setId] = useState<number | undefined>(undefined);
  const [data, setData] = useState<IDashboardData | undefined>(undefined);
  const [currentAccounts, setCurrentAccounts] = useState<IEnum[]>([]);
  const [policyModels, setPolicyModels] = useState<IEnum[]>([]);
  const [policyTypes, setPolicyTypes] = useState<IEnum[]>([]);

  const dashboardService = useMemo(
    () => new DashboardService(executeService),
    [executeService],
  );
  const reconciliationService = useMemo(
    () => new ReconciliationService(executeService, ''),
    [executeService],
  );
  const enumService = useMemo(
    () => new EnumService(executeService),
    [executeService],
  );

  const columnsForExpiringPolicies =
    getColumnsForExpiringPolicies(policyModels);
  const columnsForReconciliationDashboard =
    getColumnsForReconciliationDashboard(organizations, currentAccounts);

  const handleFetchEnums = async () => {
    const [currentAccounts, policyModels] = await Promise.all([
      enumService.fetchCurrentAccounts(),
      enumService.fetchPolicyModels(),
    ]);

    if (Array.isArray(currentAccounts)) {
      setCurrentAccounts(currentAccounts);
    }

    if (Array.isArray(policyModels)) {
      setPolicyModels(policyModels);
    }

    if (Array.isArray(policyTypes)) {
      setPolicyTypes(policyTypes);
    }
  };

  const handleFetchData = async (id: number) => {
    const [
      expiringPolicies,
      overview,
      policyDistributions,
      reconciliationDashboard,
    ] = await Promise.all([
      dashboardService.fetchExpiringPolicies(id),
      dashboardService.fetchOverview(id),
      dashboardService.fetchPolicyDistributions(id),
      reconciliationService.fetchReconciliationDashboard(id),
    ]);

    setData({
      expiringPolicies:
        expiringPolicies && 'results' in expiringPolicies
          ? expiringPolicies
          : undefined,
      overview: Array.isArray(overview) ? overview : [],
      policyDistributions:
        policyDistributions && !('statusText' in policyDistributions)
          ? policyDistributions
          : undefined,
      reconciliationDashboard:
        reconciliationDashboard && 'results' in reconciliationDashboard
          ? reconciliationDashboard
          : undefined,
    });
  };

  const handleChangeDropdown = (value: string) => {
    setId(+value);
  };

  useEffect(() => {
    handleFetchEnums();
  }, []);

  useEffect(() => {
    if (userInfo.organization !== 1) {
      setId(userInfo.organization);
    } else {
      const foundOrganizations = organizations.find(
        (item) => item.name === 'LC Waikiki',
      );
      setId(foundOrganizations?.id);
    }
  }, [userInfo.organization, organizations]);

  useEffect(() => {
    if (id) {
      handleFetchData(id);
    }
  }, [id]);

  return (
    <Container>
      <PageTitle title="Genel Bakış" />
      <Row>
        <Col>
          <SDropdown
            value={id?.toString()}
            className={globalStyles.width200px}
            placeholder="Şirket Seçiniz"
            options={organizations.map((item) => ({
              value: item.id.toString(),
              label: item.name,
            }))}
            onChange={handleChangeDropdown}
          />
        </Col>
      </Row>
      <Row>
        <Col
          md={8}
          lg={8}
          style={{
            padding: 16,
            border: '1px solid #A2ABB2',
            borderRadius: 6,
          }}
        >
          <Flex vertical justify="space-between" style={{ height: '100%' }}>
            <PageTitle isSubTitle title="Bekleyen İşlemler" />
            <Flex vertical gap={16} align="center">
              {data?.overview.map((item) => (
                <Flex key={item.status} gap={16}>
                  <StatusCard
                    count={item.total_count}
                    status={item.status as StatusEnum}
                    policy_types={item.policy_types}
                    policyTypesEnum={policyModels}
                  />
                </Flex>
              ))}
            </Flex>
            <Typography className={styles.description}>
              Henüz sonuçlanmamış talepler listelenmektedir.
            </Typography>
          </Flex>
        </Col>
        <Col md={1} lg={1}></Col>
        <Col
          md={15}
          lg={15}
          style={{
            padding: 16,
            border: '1px solid #A2ABB2',
            borderRadius: 6,
          }}
        >
          <PageTitle isSubTitle title="Vadesi Yaklaşan Poliçeler" />
          <DataTable<IExpiringPolicies>
            scrollable
            columns={columnsForExpiringPolicies}
            dataSource={data?.expiringPolicies?.results ?? []}
            isFooterDisplayed={false}
          />
          <Typography className={styles.description}>
            Önümüzdeki 30 gün içerisinde vadesi sona erecek poliçeler
            listelenmektedir
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col
          md={15}
          lg={15}
          style={{
            padding: 12,
            border: '1px solid #A2ABB2',
            borderRadius: 6,
          }}
        >
          <PageTitle isSubTitle title="Muhasebe" />
          <DataTable<IReconciliationDashboard>
            scrollable
            columns={columnsForReconciliationDashboard}
            dataSource={data?.reconciliationDashboard?.results ?? []}
            isFooterDisplayed={false}
          />
          <Typography className={styles.description}>
            Ay sonu itibariyle vadesi geçmiş borç bakiyelerine istinaden ödeme
            yapılması rica olunur. Ödeme yapıldıysa dikkate almayınız. Cari
            hesap bakiyeleriniz her ayın son günü baz alınarak
            güncellenmektedir.
          </Typography>
        </Col>
        <Col lg={1} md={1}></Col>
        <Col
          md={8}
          lg={8}
          style={{
            padding: 16,
            border: '1px solid #A2ABB2',
            borderRadius: 6,
          }}
        >
          <PageTitle isSubTitle title="Poliçe Dağılımı" />
          <PieChart policyDistributions={data?.policyDistributions} />
          <Typography className={styles.description}>
            Adet bazlı dağılımı göstermektedir.
          </Typography>
        </Col>
      </Row>
    </Container>
  );
};

export default Mainpage;
